import { useEffect } from "react";
import { CALL_API_METHODS } from "repoV2/constants/apis";
import {
    DAY_IN_MILLISECONDS,
    WEEK_IN_MILLISECONDS,
} from "repoV2/features/Common/modules/DateTime/modules/Time/Time.constants";
import { useOnIpGeolocResolutionInLegacyCode } from "@Redux/sagas/useOnIpGeolocResolutionInLegacyCode";
import { LOCAL_STORAGE_KEYS } from "repoV2/features/Common/modules/Storage/Storage.constants";
import {
    get1stTimezoneDataFromCountryCode,
    getTimezoneDataFromTimezone,
} from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { callApi } from "repoV2/utils/common/api/callApi";
import { logError } from "repoV2/utils/common/error/error";
import {
    getLocalStorageItem,
    getSessionStorageItem,
    setLocalStorageItem,
    setSessionStorageItem,
} from "repoV2/utils/common/storage/getterAndSetters";
import { SESSION_STORAGE_KEYS } from "repoV2/constants/common/storage/sessionStorage";
import {
    IIpGeolocDetails,
    IUserIpDetailsForAnalytics,
} from "./UserIp.interfaces";
import {
    CLOUDFLARE_IP_GEOLOC_API_URL,
    GEOLOC_DB_API_URL,
    GET_USER_IP_DETAILS_API_URL,
    default_user_ip_details_for_analytics_info,
} from "./UserIp.constants";
import { useIpGeolocDetailsStore } from "./UserIp.store";
import { checkIsValidCountryCode } from "../Location/utils/checkIsValidCountryCode";

/**
 * see `callPostExlyAnalytics` to understand diff b/w `UserIp` and `UserIpDetailsForAnalyticsInfo`
 */
export const getUserIp = (): string | undefined =>
    getSessionStorageItem(SESSION_STORAGE_KEYS.USER_IP);

export const setUserIp = (value?: string) =>
    setSessionStorageItem(SESSION_STORAGE_KEYS.USER_IP, value);

export const getUserIpDetailsForAnalyticsInfo =
    (): IUserIpDetailsForAnalytics =>
        getLocalStorageItem(
            LOCAL_STORAGE_KEYS.USER_IP_DETAILS_FOR_ANALYTICS_INFO
        ) ||
        JSON.parse(JSON.stringify(default_user_ip_details_for_analytics_info));

const setUserIpDetailsForAnalyticsInfo = (value: IUserIpDetailsForAnalytics) =>
    setLocalStorageItem(
        LOCAL_STORAGE_KEYS.USER_IP_DETAILS_FOR_ANALYTICS_INFO,
        value
    );

const _callGetUserIpDetails = () =>
    callApi({ url: GET_USER_IP_DETAILS_API_URL, method: CALL_API_METHODS.GET });

const callGetUserIpDetailsNUpdateAnalytics = () =>
    _callGetUserIpDetails().then((response: any) => {
        // Whenver this paid api is called, we should update our stored analytics info with the latest details.

        const userIpDetails: IUserIpDetailsForAnalytics = JSON.parse(
            JSON.stringify(default_user_ip_details_for_analytics_info)
        );

        const data = response?.data || {};
        userIpDetails.data.country = data.country;
        userIpDetails.data.city = data.city;
        userIpDetails.data.state = data.regionName;
        userIpDetails.metadata.updatedAt = Date.now();
        setUserIpDetailsForAnalyticsInfo(userIpDetails);

        return response;
    });

/**
 * In case the analytics is not updated for more than a week using the paid api,
 * this function ensures that it is called and the analytics data is updated.
 */
const handleUserIPDetailsForAnalytics = () => {
    /**
     * if: updatedAt value in storgae is greater than a week
     * the: call the api
     */
    const updatedAt =
        getUserIpDetailsForAnalyticsInfo().metadata?.updatedAt || 0;
    const shouldCallApi = updatedAt + WEEK_IN_MILLISECONDS < Date.now();

    if (!shouldCallApi) return;

    callGetUserIpDetailsNUpdateAnalytics().catch(err => {
        logError({
            error: err,
            occuredAt: "modals/AfterBookingQuestions",
            when: "calling callGetUserIpDetails",
            severity: "medium",
        });
    });
};

const parseCloudflareIpResponse = (
    payload: string = ""
): { [key: string]: string } => {
    const keyValues = payload.split("\n");
    let parsedPayload: { [k: string]: string } = {};
    keyValues.forEach((keyValue: string) => {
        const keyValuePair: Array<string> = keyValue.split("=");
        if (keyValuePair.length === 2) {
            parsedPayload = {
                ...parsedPayload,
                [keyValuePair[0]]: keyValuePair[1],
            };
        }
    });
    return parsedPayload;
};

const checkIsValidIpGeolocDetails = (details: IIpGeolocDetails | null) => {
    if (!details) return false;

    const data = details.data || {};
    const countryCodeVal = data.countryCode;
    const ipVal = data.ip;
    const updatedAt = details.metadata?.updatedAt || 0;
    if (
        updatedAt + DAY_IN_MILLISECONDS > Date.now() &&
        ipVal &&
        countryCodeVal &&
        checkIsValidCountryCode(countryCodeVal)
    ) {
        return true;
    }

    return false;
};

export const getCachedIpGeolocDetails: () => IIpGeolocDetails | null = () => {
    const cache = getLocalStorageItem(LOCAL_STORAGE_KEYS.IP_GEOLOCATION_DATA);

    if (checkIsValidIpGeolocDetails(cache)) return cache;

    return null;
};

const setCachedIpGeolocDetails = (newCache: IIpGeolocDetails | null) => {
    if (!checkIsValidIpGeolocDetails(newCache)) return;

    setLocalStorageItem(LOCAL_STORAGE_KEYS.IP_GEOLOCATION_DATA, newCache);
};

/**
 * function to call on succes of fetching ip geolocation details
 */
const generateNewIpGeolocDetails = ({
    ip,
    countryCode,
    timezone,
}: {
    ip: string;
    countryCode: string;
    timezone?: string;
}): IIpGeolocDetails => {
    const details = {
        data: {
            ip,
            countryCode,
            timezone:
                getTimezoneDataFromTimezone(timezone)?.name ||
                get1stTimezoneDataFromCountryCode(countryCode)?.name,
        },
        metadata: { updatedAt: Date.now() },
    };

    if (!checkIsValidIpGeolocDetails(details)) {
        throw new Error(`Invalid details : ${JSON.stringify(details)}`);
    }

    return details;
};

const fetchIpGeolocDetails = async (): Promise<IIpGeolocDetails | null> => {
    const cache = getCachedIpGeolocDetails();
    if (cache) return cache;

    let details: IIpGeolocDetails | null = null;

    // fetch ip and countryCode
    try {
        const response = parseCloudflareIpResponse(
            await (await fetch(CLOUDFLARE_IP_GEOLOC_API_URL)).text()
        );
        details = generateNewIpGeolocDetails({
            ip: response.ip,
            countryCode: response.loc,
        });
    } catch (e) {
        logError({
            error: e,
            occuredAt: "repoV2/utils/common/analytics/userIp.ts",
            when: "calling 1st api in fetchIpLogic",
        });
    }

    // if 1st api failed, fetch from 2nd api
    if (!details) {
        try {
            const response =
                (await callGetUserIpDetailsNUpdateAnalytics())?.data || {};
            details = generateNewIpGeolocDetails({
                ip: response.query,
                countryCode: response.countryCode,
                timezone: response.timezone,
            });
        } catch (e) {
            logError({
                error: e,
                occuredAt: "repoV2/utils/common/analytics/userIp.ts",
                when: "calling 2nd api in fetchIpLogic",
            });
        }
    }
    // if 2nd api failed, fetch from 3rd api
    if (!details) {
        try {
            const response =
                (await (await fetch(GEOLOC_DB_API_URL)).json()) || {};
            details = generateNewIpGeolocDetails({
                ip: response.IPv4,
                countryCode: response.country_code,
            });
        } catch (e) {
            logError({
                error: e,
                occuredAt: "repoV2/utils/common/analytics/userIp.ts",
                when: "calling 3rd api in fetchIpLogic",
            });
        }
    }

    setCachedIpGeolocDetails(details);

    return details;
};

export const useFetchIpGeolocDetails = () => {
    const { onIpGeolocResolutionInLegacyCode } =
        useOnIpGeolocResolutionInLegacyCode();

    useEffect(() => {
        (async () => {
            try {
                const response = await fetchIpGeolocDetails();
                const data = response?.data;

                // default values indicate that the data is not available
                const countryCode = data?.countryCode || "";
                const ip = data?.ip || "";
                const timezone = data?.timezone || "";
                const metadata = response?.metadata;
                const updatedAt = metadata?.updatedAt || Date.now();

                useIpGeolocDetailsStore.getState().set({
                    data: { ip, countryCode, timezone },
                    metadata: { updatedAt },
                });

                onIpGeolocResolutionInLegacyCode({ ip, countryCode });

                handleUserIPDetailsForAnalytics();
            } catch (e) {
                logError({
                    error: e,
                    occuredAt:
                        "repoV2/features/Common/modules/UserIp/UserIp.utils.ts",
                    when: "calling fetchIpGeolocDetails in useEffect",
                });
            }
        })();

        const resetIpGeolocDetails = useIpGeolocDetailsStore.getState().reset;
        return resetIpGeolocDetails;
    }, []);
};
